import { Model } from '../models/model.class';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from 'environments/environment';

export interface ModelSaveResponse<M extends Model<M>> {
    success: boolean;
    obj: M;
}

@Injectable()
export abstract class ModelService<M extends Model<M>> {
    /**
     * The Path appended to the API_URL in endpointUrl.
     *
     * Override in child class.
     */
    protected urlPath = '';

    /**
     * Returns the endpointUrl, including the API_URL with urlPath appended.
     */
    get endpointUrl(): string {
        return environment.API_URL + this.urlPath;
    }

    constructor( public http: HttpClient ) { }

    get(id: number): Observable<M> {
        return this.httpGetResponse(id).pipe(
            map(body => this.createFromResponseData(body))
        );
    }

    getAll(params = {}): Observable<M[]> {
        return this.httpGetAllResponse(params).pipe(
            map(body => body.map((modelInstanceValues) => this.createFromResponseData(modelInstanceValues)))
        );
    }

    // Alias for get
    find(params = {}): Observable<M[]> {
        return this.httpFindResponse(params).pipe(
            map(body => this.createFromFindResponseData(body))
        );
    }

    save(model: M): Observable<M> {
        const body = model.json();
        let request$;
        if (model.hasOwnProperty('id')) {
            request$ = this.http.put(this.putUrl(model), body);
        } else {
            request$ = this.http.post(this.postUrl(model), body);
        }
        return request$.pipe(map(this.createFromResponseData.bind(this)));
    }

    delete(model: M): Observable<void> {
        return this.httpDeleteResponse(model);
    }

    protected putUrl(_model: M): string {
        return `${this.endpointUrl}/${_model.id}`;
    }

    protected postUrl(_model: M): string {
        return `${this.endpointUrl}`;
    }

    protected getUrl(id: number): string {
        return `${this.endpointUrl}/${id}`;
    }

    protected findUrl(): string {
        return this.endpointUrl;
    }

    protected getAllUrl(): string {
        return this.endpointUrl;
    }

    protected deleteUrl(model: M): string {
        return `${this.endpointUrl}/${model.id}`;
    }

    protected httpGetResponse(id: number): Observable<any> {
        return this.http.get(this.getUrl(id));
    }

    protected httpFindResponse(params): Observable<any> {
        const httpParams = this.httpParams(params);
        return this.http.get(this.findUrl(), {
            params: httpParams
        });
    }

    protected httpGetAllResponse(params): Observable<any> {
        // Search.php middleware expects "query" input
        if (params.query) {
            params = Object.assign({}, params, {
                query: JSON.stringify(params.query)
            });
        }
        return this.http.get(this.getAllUrl(), {
            params: this.httpParams(params)
        });
    }

    protected httpDeleteResponse(model: M): Observable<void> {
        return this.http.delete<void>(this.deleteUrl(model));
    }

    protected abstract createFromResponseData(responseData: any): M;

    protected createFromFindResponseData(responseData: any): M[] {
        return responseData.map(data => this.createFromResponseData(data));
    }

    protected httpParams(params): HttpParams {
        let httpParams = new HttpParams();
        for (const key in params) {
            if (params.hasOwnProperty(key)) {
                httpParams = httpParams.set(key, params[key]);
            }
        }
        return httpParams;
    }
}
